import { Link } from 'react-router';
import type { PersonRating } from '@fewerbags/schema';
import { formatAvg } from '../../pages/recipes/taxonomy';
import { RatingInput } from './StarRating';

/**
 * Every person in the household and their stars (D26/D30). Anyone signed in
 * can rate for anyone — including people without an account, such as young
 * children — and a rating entered on someone's behalf says who entered it.
 * The household average covers these people only; other households show up
 * solely in the anonymous aggregate elsewhere on the page (D12).
 */
export function PersonRatings({
  people,
  householdRating,
  pendingPersonId,
  onRate,
  onClear,
}: {
  people: PersonRating[];
  householdRating: { avg: number | null; count: number };
  /** The person whose rating is being saved, if any (disables just that row). */
  pendingPersonId: string | null;
  onRate: (personId: string, stars: number) => void;
  onClear: (personId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-ink">
        <span className="font-semibold">Household average: </span>
        {householdRating.avg === null ? (
          <span className="text-muted">no ratings yet</span>
        ) : (
          <>
            <span className="font-semibold text-orange-ink">{formatAvg(householdRating.avg)}</span>
            <span className="text-muted">
              {' '}
              out of 5 from {householdRating.count} of {people.length}{' '}
              {people.length === 1 ? 'person' : 'people'}
            </span>
          </>
        )}
      </p>
      <ul className="flex flex-col divide-y divide-line" aria-label="Ratings by family member">
        {people.map((p) => (
          <li key={p.personId} className="flex flex-col gap-1 py-2">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-ink">
                {p.name}
                {p.isMe ? <span className="text-muted">(you)</span> : null}
                {p.hasAccount ? null : (
                  <span className="rounded-full bg-orange-soft px-2 py-0.5 text-xs font-medium text-orange-ink">
                    no account
                  </span>
                )}
              </span>
              <RatingInput
                value={p.stars}
                busy={pendingPersonId === p.personId}
                label={p.isMe ? 'Your rating' : `Rating for ${p.name}`}
                onRate={(stars) => onRate(p.personId, stars)}
                onClear={() => onClear(p.personId)}
              />
            </div>
            {p.ratedByName ? <p className="text-xs text-muted">Rated by {p.ratedByName}</p> : null}
            {p.notes ? <p className="text-xs text-muted">“{p.notes}”</p> : null}
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted">
        Rate for anyone in the family.{' '}
        <Link
          to="/settings/household#family-members"
          className="font-medium text-brand underline-offset-2 hover:underline"
        >
          Add family members without an account
        </Link>
      </p>
    </div>
  );
}
